import { Trans, useLingui } from "@lingui/react/macro";
import { Badge } from "@offstreetparking/design-system/dataDisplay/Badge";
import { formatCurrency, formatDate } from "@offstreetparking/design-system/formatters";
import { CircleDollarSignIcon } from "@offstreetparking/design-system/icons/CircleDollarSignIcon";
import { useLocale } from "@offstreetparking/design-system/providers";
import { Text } from "@offstreetparking/design-system/typography";
import type { PaymentScheduleSummary } from "@offstreetparking/domain/value/payment-schedule";
import { ParkingDetailsRow } from "./ParkingDetailsRow";
import { type ProrationInterval, getProration } from "./getProration";
type ParkingCostProps = {
	totalCents?: number;
	currency: string;
	email?: string;
	fullTermIntervals?: ProrationInterval[];
	timezone?: string;
	paymentSchedule?: PaymentScheduleSummary;
};
export const ParkingCost = ({
	totalCents = 0,
	currency,
	email,
	fullTermIntervals,
	timezone,
	paymentSchedule,
}: ParkingCostProps) => {
	const { t } = useLingui();
	const { locale } = useLocale();
	const proration = getProration({ fixedIntervals: fullTermIntervals, timezone });
	const proratedDisplay =
		proration.isProrated && proration.fullTermStartISO && proration.fullTermEndISO && timezone
			? {
					fullTermStartISO: proration.fullTermStartISO,
					fullTermEndISO: proration.fullTermEndISO,
					timezone,
				}
			: null;
	const fullTermStart = proratedDisplay
		? formatDate({
				date: proratedDisplay.fullTermStartISO,
				formatType: "Abbreviated_Date",
				timezone: proratedDisplay.timezone,
			})
		: "";
	const fullTermEnd = proratedDisplay
		? formatDate({
				date: proratedDisplay.fullTermEndISO,
				formatType: "Abbreviated_Date",
				timezone: proratedDisplay.timezone,
			})
		: "";
	if (paymentSchedule) {
		const perPeriodAmount = formatCurrency({
			valueInCents: paymentSchedule.perPeriodAmountInCents,
			currency: currency,
			locale: locale,
		});
		return (
			<ParkingDetailsRow icon={CircleDollarSignIcon} iconLabel={t`Parking Cost`}>
				<div className="w-full space-y-2">
					<div className="flex flex-wrap items-center gap-2">
						<Text weight="bold" As="span">
							{paymentSchedule.isOngoing
								? t`${perPeriodAmount} per pay period`
								: formatCurrency({
										valueInCents: paymentSchedule.deductedTotalInCents,
										currency: currency,
										locale: locale,
									})}
						</Text>
						<Badge variant="info">{t`Charged via payroll`}</Badge>
					</div>
					<Text size="sm" intent="muted">
						{paymentSchedule.isOngoing
							? t`Deducted from your pay every pay period until your permit is canceled.`
							: t`${perPeriodAmount} will be deducted from your pay each pay period.`}
					</Text>
				</div>
			</ParkingDetailsRow>
		);
	}
	return (
		<ParkingDetailsRow icon={CircleDollarSignIcon} iconLabel={t`Parking Cost`}>
			<div className="w-full space-y-2">
				<div className="flex items-center gap-2">
					<Text weight="bold">
						{formatCurrency({
							valueInCents: totalCents,
							currency: currency,
							locale: locale,
						})}
					</Text>
					{proratedDisplay && <Badge variant="success">{t`Prorated price`}</Badge>}
				</div>
				{proratedDisplay && (
					<Text size="sm" intent="muted">
						{t`Full permit term is ${fullTermStart} – ${fullTermEnd}. Your price is prorated based on the remaining permit term.`}
					</Text>
				)}
				{email && (
					<div className="flex gap-1 text-sm">
						<Trans>
							Receipt emailed to: <b className="font-semibold">{email}</b>
						</Trans>
					</div>
				)}
			</div>
		</ParkingDetailsRow>
	);
};
